import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsMcpV1WixPaymentsAccountServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/payments/v1/wix-payments-account',
        destPath: '/v1/wix-payments-account',
      },
      {
        srcPath: '/payments/mcp/v1/wix-payments-account',
        destPath: '/v1/wix-payments-account',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/payments/mcp/v1/wix-payments-account',
        destPath: '/v1/wix-payments-account',
      },
    ],
    _: [
      {
        srcPath: '/payments/mcp/v1/wix-payments-account',
        destPath: '/v1/wix-payments-account',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_wix-payments-account';

/**
 * Connects a Wix Payments account to the site.
 *
 *
 * The call fails if:
 * - The site's location
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
 * is outside the USA.
 * - A Wix Payments account is already connected to the site.
 * - You don't specify `{"tosAccepted": true}`.
 *
 * The connected Wix Payments account has the following limitations, which site
 * owners can manage in their dashboard:
 *
 * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
 * - To receive payments, site owners must complete onboarding to their Wix
 * Payments account in the site's dashboard.
 */
export function connectWixPaymentsAccount(
  payload: object
): RequestOptionsFactory<any> {
  function __connectWixPaymentsAccount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.mcp.v1.wix_payments_account',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.mcp.v1.WixPaymentsAccountService.ConnectWixPaymentsAccount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsMcpV1WixPaymentsAccountServiceUrl({
        protoPath: '/v1/wix-payments-account/connect',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __connectWixPaymentsAccount;
}

/**
 * Connects a Wix Payments account to the site.
 *
 *
 * The call fails if:
 * - The site's location
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
 * is outside the USA.
 * - A Wix Payments account is already connected to the site.
 * - You don't specify `{"tosAccepted": true}`.
 *
 * The connected Wix Payments account has the following limitations, which site
 * owners can manage in their dashboard:
 *
 * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
 * - To receive payments, site owners must complete onboarding to their Wix
 * Payments account in the site's dashboard.
 */
export function connectWixPaymentsAccountBase44(
  payload: object
): RequestOptionsFactory<any> {
  function __connectWixPaymentsAccountBase44({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.mcp.v1.wix_payments_account',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.mcp.v1.WixPaymentsAccountService.ConnectWixPaymentsAccountBase44',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsMcpV1WixPaymentsAccountServiceUrl({
        protoPath: '/v1/wix-payments-account/connect-base44',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __connectWixPaymentsAccountBase44;
}

/**
 * Sets up a Base44 site with an API key, without connecting a Wix Payments account.
 *
 * This endpoint:
 * - Creates or publishes the metasite
 * - Installs required apps
 * - Adds the "accept_payments" premium feature
 * - Creates an API key for the site
 * - Persists the Base44 app record
 *
 * Use `ConnectWixPaymentsAccountBase44` to also connect a Wix Payments account.
 */
export function setupBase44Site(payload: object): RequestOptionsFactory<any> {
  function __setupBase44Site({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.mcp.v1.wix_payments_account',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.mcp.v1.WixPaymentsAccountService.SetupBase44Site',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsMcpV1WixPaymentsAccountServiceUrl({
        protoPath: '/v1/wix-payments-account/setup-base44-site',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __setupBase44Site;
}
